import React from "react";
import { Input } from "@/Components/ui/input";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/Components/ui/select";
import { Switch } from "@/Components/ui/switch";
import { Label } from "@/Components/ui/label";

export type StandardFieldConfig<T> = {
   key: keyof T;
   label: string;
   type: "text" | "number" | "select" | "switch";
   options?: { value: string | number; label: string }[];
};

export type CustomFieldConfig<T> = {
   key: string;
   label: string;
   type: "custom";
   render: (
      item: T,
      onChange: (field: keyof T, value: any) => void
   ) => React.ReactNode;
};

type GenericEditorProps<T> = {
   item: T;
   config: StandardFieldConfig<T>[];
   customConfig?: CustomFieldConfig<T>[];
   onChange: (field: keyof T, value: any) => void;
   title: string;
};

export function GenericEditor<T>({
   item,
   config,
   customConfig = [],
   onChange,
   title,
}: GenericEditorProps<T>) {
   return (
      <div className="bg-white shadow-md rounded-lg p-6 space-y-6 w-96">
         <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
         <div className="space-y-4">
            {config.map((field) => (
               <div key={String(field.key)}>
                  <Label className="block text-sm font-medium text-gray-700 mb-1">
                     {field.label}
                  </Label>
                  {field.type === "text" && (
                     <Input
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={(item[field.key] as string) || ""}
                        onChange={(e) => onChange(field.key, e.target.value)}
                     />
                  )}
                  {field.type === "number" && (
                     <Input
                        type="number"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={(item[field.key] as number) || 0}
                        onChange={(e) =>
                           onChange(field.key, parseFloat(e.target.value))
                        }
                     />
                  )}
                  {field.type === "select" && (
                     <Select
                        value={String(item[field.key])}
                        onValueChange={(value) => onChange(field.key, value)}
                     >
                        <SelectTrigger>
                           <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                           {field.options?.map((option) => (
                              <SelectItem
                                 key={option.value}
                                 value={String(option.value)}
                              >
                                 {option.label}
                              </SelectItem>
                           ))}
                        </SelectContent>
                     </Select>
                  )}
                  {field.type === "switch" && (
                     <Switch
                        checked={item[field.key] as boolean}
                        onCheckedChange={(value) => onChange(field.key, value)}
                     />
                  )}
               </div>
            ))}
            {customConfig.map((field) => (
               <div key={field.key}>
                  <Label className="block text-sm font-medium text-gray-700 mb-1">
                     {field.label}
                  </Label>
                  {field.render(item, onChange)}
               </div>
            ))}
         </div>
      </div>
   );
}
